import { useState, FormEvent } from "react";

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "message") {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setStatus("sending");
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch("https://readdy.ai/api/form/d7kf1o50lfokr4m6jfg0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({ name: "", email: "", phone: "", interest: "", message: "" });
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="bg-brand-cream py-24 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="font-display text-brand-gold text-xs tracking-[4px] uppercase block mb-4">Let&apos;s Connect</span>
          <h2 className="font-serif text-4xl md:text-5xl text-brand-darkbrown font-bold">
            Get in <em>Touch</em>
          </h2>
          <p className="font-sans text-brand-muted mt-4 max-w-xl mx-auto text-sm leading-relaxed">
            Ready to furnish your home with a piece of history? Reach out to our team and we&apos;ll guide you
            through our collections or create a custom design just for you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <a
              href="https://wa.me/923335281400"
              className="flex items-center gap-5 p-6 bg-brand-beige border border-brand-gold/20 rounded-lg hover:border-brand-gold/50 transition-all duration-300 cursor-pointer group"
              rel="nofollow"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-brand-gold/10 rounded-full group-hover:bg-brand-gold/20 transition-colors duration-300">
                <i className="ri-whatsapp-line text-brand-gold text-2xl" />
              </div>
              <div>
                <p className="font-serif text-brand-darkbrown font-bold">WhatsApp</p>
                <p className="font-sans text-brand-muted text-sm">0333 5281400</p>
              </div>
              <i className="ri-arrow-right-line text-brand-gold ml-auto group-hover:translate-x-1 transition-transform duration-300" />
            </a>

            <div className="flex items-center gap-5 p-6 bg-brand-beige border border-brand-gold/20 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-brand-gold/10 rounded-full">
                <i className="ri-phone-line text-brand-gold text-2xl" />
              </div>
              <div>
                <p className="font-serif text-brand-darkbrown font-bold">Phone</p>
                <p className="font-sans text-brand-muted text-sm">0333 5281400</p>
              </div>
            </div>

            <div className="flex items-center gap-5 p-6 bg-brand-beige border border-brand-gold/20 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-brand-gold/10 rounded-full">
                <i className="ri-map-pin-line text-brand-gold text-2xl" />
              </div>
              <div>
                <p className="font-serif text-brand-darkbrown font-bold">Visit Us</p>
                <p className="font-sans text-brand-muted text-sm">M.Hayat &amp; Co. Near Chaye Khana, Bahria Phase 7, Rawalpindi</p>
              </div>
            </div>

            <div className="flex items-center gap-5 p-6 bg-brand-beige border border-brand-gold/20 rounded-lg">
              <div className="w-12 h-12 flex items-center justify-center bg-brand-gold/10 rounded-full">
                <i className="ri-time-line text-brand-gold text-2xl" />
              </div>
              <div>
                <p className="font-serif text-brand-darkbrown font-bold">Business Hours</p>
                <p className="font-sans text-brand-muted text-sm">Mon–Sat: 9AM – 8PM</p>
              </div>
            </div>

            {/* Social */}
            <div className="flex items-center gap-3 pt-2">
              {[
                { icon: "ri-instagram-line", href: "https://www.instagram.com/m.hayatandco/" },
                { icon: "ri-facebook-circle-line", href: "#" },
                { icon: "ri-pinterest-line", href: "#" },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  className="w-10 h-10 flex items-center justify-center border border-brand-brown/30 rounded-full text-brand-brown hover:border-brand-gold hover:text-brand-gold transition-all duration-300 cursor-pointer"
                  rel="nofollow"
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="bg-brand-beige border border-brand-gold/15 rounded-lg p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-display text-xs tracking-widest uppercase text-brand-darkbrown mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full Name"
                    className="w-full bg-brand-cream border border-brand-gold/20 rounded-md px-4 py-3 text-sm font-sans text-brand-darkbrown placeholder-brand-muted/50 focus:outline-none focus:border-brand-gold transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="block font-display text-xs tracking-widest uppercase text-brand-darkbrown mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full bg-brand-cream border border-brand-gold/20 rounded-md px-4 py-3 text-sm font-sans text-brand-darkbrown placeholder-brand-muted/50 focus:outline-none focus:border-brand-gold transition-colors duration-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-display text-xs tracking-widest uppercase text-brand-darkbrown mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+92 300 000 0000"
                    className="w-full bg-brand-cream border border-brand-gold/20 rounded-md px-4 py-3 text-sm font-sans text-brand-darkbrown placeholder-brand-muted/50 focus:outline-none focus:border-brand-gold transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="block font-display text-xs tracking-widest uppercase text-brand-darkbrown mb-2">
                    Interested In
                  </label>
                  <select
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    className="w-full bg-brand-cream border border-brand-gold/20 rounded-md px-4 py-3 text-sm font-sans text-brand-darkbrown focus:outline-none focus:border-brand-gold transition-colors duration-200 cursor-pointer"
                  >
                    <option value="">Select Category</option>
                    <option value="Sofa Sets">Sofa Sets</option>
                    <option value="Bed Frames">Bed Frames</option>
                    <option value="Dining Tables">Dining Tables</option>
                    <option value="Wardrobes">Wardrobes</option>
                    <option value="Office Furniture">Office Furniture</option>
                    <option value="Custom Design">Custom Design</option>
                    <option value="Full Home Furnishing">Full Home Furnishing</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-display text-xs tracking-widest uppercase text-brand-darkbrown mb-2">
                  Your Message *
                </label>
                <textarea
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements, space dimensions, or any special requests..."
                  rows={5}
                  maxLength={500}
                  className="w-full bg-brand-cream border border-brand-gold/20 rounded-md px-4 py-3 text-sm font-sans text-brand-darkbrown placeholder-brand-muted/50 focus:outline-none focus:border-brand-gold transition-colors duration-200 resize-none"
                />
                <p className={`text-xs font-sans mt-1 text-right ${charCount >= 500 ? "text-red-500" : "text-brand-muted"}`}>
                  {charCount}/500
                </p>
              </div>

              {/* Status Messages */}
              {status === "success" && (
                <div className="bg-green-50 border border-green-200 rounded-md px-4 py-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-green-500 text-lg" />
                  <p className="font-sans text-sm text-green-700">Your message has been sent! We&apos;ll get back to you soon.</p>
                </div>
              )}
              {status === "error" && (
                <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500 text-lg" />
                  <p className="font-sans text-sm text-red-700">Something went wrong. Please try again.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="whitespace-nowrap w-full font-display text-sm tracking-widest uppercase py-4 bg-transparent border-[1.5px] border-brand-gold text-brand-gold rounded-full hover:bg-brand-gold hover:text-brand-darkbrown transition-all duration-300 cursor-pointer disabled:opacity-60"
              >
                {status === "sending" ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
